import { createContext, createContextHook } from '@/utils';
import type { QueryError } from '@/utils/errors';
import type { Category, CompleteCategory } from './types';

type MutationCallbacks = {
  onSuccess?: () => void;
  onError?: (error: QueryError) => void;
};

type UpdateCallbacks = {
  onSuccess?: () => void;
  onError?: (
    error: QueryError,
    variables: unknown,
    context?: { prevCategory?: Category }
  ) => void;
};

/**
 * Supplies the category list and CRUD operations to the shared category
 * components. Schedules and Experiences each provide their own implementation
 * (ScheduleCategoryDataProvider / CategoryProvider) so the
 * presentational components remain agnostic to the underlying API.
 */
export type CategoryData = {
  categories: Category[];
  createCategory: (
    category: CompleteCategory,
    options?: MutationCallbacks
  ) => void;
  isCreatePending: boolean;
  updateCategory: (category: Category, options?: UpdateCallbacks) => void;
  removeCategory: (categoryId: string, options?: MutationCallbacks) => void;
  updateSortOrder: (categories: Category[]) => void;
  /**
   * Optional consumer-specific error handling for category deletion. Return
   * `true` to indicate the error was handled and suppress the default toast.
   */
  handleRemoveError?: (error: QueryError) => boolean;
};

export const CategoryDataContext = createContext<CategoryData | undefined>(
  undefined,
  'CategoryDataContext'
);

export const useCategoryData = createContextHook(CategoryDataContext);
